import {inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {LocalStorageService} from './local-storage.service';

const GCLID_KEY = 'gclid';
const EXPIRY_MS = 90 * 24 * 3600 * 1000;

interface GclidEntry {
  gclid: string;
  capturedAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class GclidService {

  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  constructor(private storageService: LocalStorageService) {}

  /** Returns window.location.search; extracted as a seam for testing. */
  private getSearch(): string {
    return window.location.search;
  }

  /**
   * Deliberate semantic split from SignupSourceService: gclid feeds offline
   * conversion upload to Google Ads, which attributes last-click within its
   * own ~30-day click window, so this stays last-click (overwrite on every
   * new gclid). SignupSourceService stays first-touch for channel reporting.
   * Do not "fix" this asymmetry again — see b6a2b077 / its revert.
   */
  capture(): void {
    if (!this.isBrowser) {
      return;
    }
    const gclid = new URLSearchParams(this.getSearch()).get('gclid');
    if (gclid) {
      this.storageService.setItem(GCLID_KEY, {gclid, capturedAt: Date.now()} as GclidEntry);
    }
  }

  getValidGclid(): string | null {
    if (!this.isBrowser) {
      return null;
    }
    const entry: GclidEntry | null = this.storageService.getItem(GCLID_KEY);
    if (!entry) {
      return null;
    }
    if (Date.now() - entry.capturedAt > EXPIRY_MS) {
      this.storageService.removeItem(GCLID_KEY);
      return null;
    }
    return entry.gclid;
  }

  clear(): void {
    this.storageService.removeItem(GCLID_KEY);
  }
}
